import { Chip } from '@nespresso-design-system/components';
import cn from 'classnames';
import SkeletonShape from '@/widgets/components/controls/Skeleton/SkeletonShape';
import styles from './SortPicker.module.scss';
import type { FiltersAndSort } from '@/widgets/services/FilteringAndSortingService.ts';
import FilterPicker from '../FilterPicker/FilterPicker.tsx';
import useTranslations from '@/widgets/hooks/useTranslations.tsx';
import type { Options } from '@/widgets/ProductList/config/index.ts';
import { trackSortingApplied } from '@/widgets/ProductList/Tracking.ts';
import useMediaQuery from '@/widgets/hooks/useMediaQuery.tsx';
import type { Filter } from '@/domain/FilteringAndSorting.ts';

export type SortPickerProps = {
  sorting: FiltersAndSort['sorting'];
  class?: string;
  id: string;
};

export default function SortPicker({
  sorting,
  class: className,
  id
}: SortPickerProps) {
  const { t } = useTranslations<Options>();
  const { isMobile } = useMediaQuery();
  const options = sorting.sortOptions.value;
  const currentSort = sorting.currentSort.value;
  const sortLabel = t('productList.sort.label');
  const recommendedText = t('productList.sort.recommended');
  const filter = {
    id,
    key: 'sorting' as Filter['key'],
    label: sortLabel,
    options: []
  };

  if (isMobile) return null;

  return (
    <FilterPicker
      filter={filter}
      isSelected
      position="right"
      class={cn(styles.container, className)}
    >
      {(toggle) => (
        <ul class={styles.optionsList}>
          <li key="recommended">
            <Chip
              aria-label={recommendedText}
              label={recommendedText}
              isActive={!currentSort}
              onClick={() => {
                sorting.clearSort();

                trackSortingApplied(
                  'recommended',
                  `${filter.key}-${filter.label}`,
                  true
                );
                toggle();
              }}
            />
          </li>
          {options.map((option) => {
            const isActive =
              currentSort?.id === option.id &&
              currentSort?.order === option.order;

            return (
              <li key={option.id}>
                <Chip
                  aria-label={option.label}
                  label={option.label}
                  isActive={isActive}
                  onClick={() => {
                    if (isActive) {
                      sorting.clearSort();
                    } else {
                      sorting.applySort(option.id);
                    }

                    trackSortingApplied(
                      option.id,
                      `${filter.key}-${filter.label}`,
                      !isActive
                    );
                    toggle();
                  }}
                />
              </li>
            );
          })}
        </ul>
      )}
    </FilterPicker>
  );
}

SortPicker.Skeleton = function Skeleton() {
  return <SkeletonShape class={styles.skeleton} />;
};
